import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject, fromEvent } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AdminConfigService } from '../../services/admin-config.service';
import { ListDataService } from '../../services/list-data.service';
import { ToastNotificationComponent } from '../../toast-notification/toast-notification.component';
import { ListItem } from '../../models/ListItem';
import { LocationListItem } from '../../models/LocationListItem';
import { AcsLocationNote } from '../models/acs.models';
import { ACS_TOAST_SUCCESS_DURATION, ACS_TOAST_ERROR_DURATION } from '../utils/acs.constants';
import { extractErrorMessage, scopedLocsToListItems, executeBulkDelete, formatBulkDeleteToast } from '../utils/acs.utils';
import { AcsDropdownState } from '../utils/acs-dropdown-state';
import { AcsDropdownService } from '../utils/acs-dropdown.service';
import { AcsBulkDeleteState } from '../utils/acs-bulk-delete-state';

@Component({
  selector: 'app-acs-location-notes',
  templateUrl: './acs-location-notes.component.html',
  styleUrls: ['../acs-shared.css', './acs-location-notes.component.css'],
  standalone: false
})
export class AcsLocationNotesComponent implements OnInit, OnDestroy {

  private _destroy$: Subject<void> = new Subject<void>();

  @ViewChild('toastNotification') toastNotification!: ToastNotificationComponent;

  locationNotes: AcsLocationNote[] = [];
  locList: ListItem[] = [];
  formLocDropdown!: AcsDropdownState;
  locationFilter: string = '';
  cityFilter: string = '';
  stateFilter: string = '';

  showDialog: boolean = false;
  isEditing: boolean = false;
  formLocationNotesId: number = 0;
  formLocationId: number | null = null;
  formLocCode: string = '';
  formOriginNotes: string = '';
  formDestNotes: string = '';
  formLocationName: string = '';
  formCity: string = '';
  formProvince: string = '';

  locationSearchName: string = '';
  locationSearchCity: string = '';
  locationSearchState: string = '';
  locationSearchResults: LocationListItem[] = [];
  locationSearched: boolean = false;
  locationResultNameFilter: string = '';
  locationResultCityStateFilter: string = '';

  get filteredLocationResults(): LocationListItem[] {
    const nameFilter: string = this.locationResultNameFilter.trim().toLowerCase();
    const cityStateFilter: string = this.locationResultCityStateFilter.trim().toLowerCase();
    if (!nameFilter && !cityStateFilter) return this.locationSearchResults;
    return this.locationSearchResults.filter(loc => {
      if (nameFilter && !(loc.locationName || '').toLowerCase().includes(nameFilter)) return false;
      if (cityStateFilter) {
        const combined: string = `${loc.city || ''}, ${loc.state || ''}`.toLowerCase();
        if (!combined.includes(cityStateFilter)) return false;
      }
      return true;
    });
  }

  hasSearched: boolean = false;
  bulkDelete = new AcsBulkDeleteState<AcsLocationNote>(n => n.locationNotesId, () => this.locationNotes);

  constructor(
    private _adminConfigService: AdminConfigService,
    private _listDataService: ListDataService,
    public dropdowns: AcsDropdownService
  ) {
    this.formLocDropdown = new AcsDropdownState('ln-form-loc', this.dropdowns, () => this.locList, '-- Select --');
  }

  ngOnInit(): void {
    this._adminConfigService.getScopedLocs().pipe(takeUntil(this._destroy$)).subscribe({
      next: (result) => this.locList = scopedLocsToListItems(result, false)
    });
    fromEvent(document, 'click').pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.dropdowns.close();
    });
  }

  selectFormLoc(loc: ListItem | null): void {
    this.formLocCode = this.formLocDropdown.select(loc);
    this.onFormLocCodeChange();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  loadNotes(): void {
    this.bulkDelete.selectedIds = new Set();
    this.hasSearched = true;
    this._adminConfigService.getLocationNotes('', this.locationFilter, this.cityFilter, this.stateFilter)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (data) => this.locationNotes = data
      });
  }

  selectNote(item: AcsLocationNote): void {
    this.isEditing = true;
    this.showDialog = true;
    this.formLocationNotesId = item.locationNotesId;
    this.formLocationId = item.locationId;
    this.formLocCode = item.locCode || '';
    this.formLocDropdown.selectedValue = this.formLocCode;
    this.formOriginNotes = item.originNotes || '';
    this.formDestNotes = item.destNotes || '';
    this.formLocationName = item.locationName || '';
    this.formCity = item.city || '';
    this.formProvince = item.province || '';
    this.clearLocationSearch();
  }

  addNote(): void {
    this.isEditing = false;
    this.showDialog = true;
    this.formLocationNotesId = 0;
    this.formLocationId = null;
    this.formLocCode = '';
    this.formLocDropdown.selectedValue = '';
    this.formOriginNotes = '';
    this.formDestNotes = '';
    this.formLocationName = '';
    this.formCity = '';
    this.formProvince = '';
    this.clearLocationSearch();
  }

  closeDialog(): void {
    this.showDialog = false;
    this.clearLocationSearch();
  }

  saveNote(): void {
    if (this.formLocationId === null) return;
    const dto: Partial<AcsLocationNote> = {
      locationNotesId: this.formLocationNotesId,
      locationId: this.formLocationId,
      locCode: this.formLocCode,
      originNotes: this.formOriginNotes,
      destNotes: this.formDestNotes
    };
    this._adminConfigService.saveLocationNote(dto).pipe(takeUntil(this._destroy$)).subscribe({
      next: (result) => {
        if (result) {
          this.toastNotification.showToast('Location note saved', ACS_TOAST_SUCCESS_DURATION, 'success');
          this.closeDialog();
          this.loadNotes();
        }
      },
      error: (err) => {
        const msg: string = extractErrorMessage(err, 'Failed to save location note');
        this.toastNotification.showToast(msg, ACS_TOAST_ERROR_DURATION, 'error');
      }
    });
  }

  searchLocations(): void {
    if (this.locationSearchName.length < 2 && this.locationSearchCity.length < 2 && this.locationSearchState.length < 2) {
      return;
    }
    this.locationResultNameFilter = '';
    this.locationResultCityStateFilter = '';
    this._listDataService.searchLocations({
      locationName: this.locationSearchName,
      city: this.locationSearchCity,
      state: this.locationSearchState,
      customerName: ''
    }).pipe(takeUntil(this._destroy$)).subscribe({
      next: (data) => {
        this.locationSearchResults = data;
        this.locationSearched = true;
      }
    });
  }

  selectLocation(loc: LocationListItem): void {
    this.formLocationId = loc.locationId;
    this.formLocationName = loc.locationName;
    this.formCity = loc.city;
    this.formProvince = loc.state;
    this.locationSearchResults = [];
    this.locationSearched = false;
    this.checkExistingNote();
  }

  onFormLocCodeChange(): void {
    this.checkExistingNote();
  }

  private checkExistingNote(): void {
    if (!this.formLocationId || !this.formLocCode || this.isEditing) return;

    this._adminConfigService.getLocationNotes(this.formLocCode, '', '', '')
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (notes) => {
          const match = notes.find(n => n.locationId === this.formLocationId);
          if (match) {
            this.isEditing = true;
            this.formLocationNotesId = match.locationNotesId;
            this.formOriginNotes = match.originNotes || '';
            this.formDestNotes = match.destNotes || '';
            this.toastNotification.showToast('Existing note found — switching to edit', ACS_TOAST_SUCCESS_DURATION, 'success');
          }
        }
      });
  }

  clearSelectedLocation(): void {
    this.formLocationId = null;
    this.formLocationName = '';
    this.formCity = '';
    this.formProvince = '';
    this.locationSearchResults = [];
    this.locationSearched = false;
  }

  // --- Bulk Delete ---

  confirmBulkDelete(): void {
    this.bulkDelete.isDeleting = true;
    const ids: number[] = [...this.bulkDelete.selectedIds];
    executeBulkDelete(ids, (id: number) => this._adminConfigService.deleteLocationNote(id))
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (result) => {
          const toast = formatBulkDeleteToast(result, 'location note');
          this.toastNotification.showToast(toast.message, toast.type === 'success' ? ACS_TOAST_SUCCESS_DURATION : ACS_TOAST_ERROR_DURATION, toast.type);
          this.bulkDelete.reset();
          this.loadNotes();
        }
      });
  }

  private clearLocationSearch(): void {
    this.locationSearchName = '';
    this.locationSearchCity = '';
    this.locationSearchState = '';
    this.locationSearchResults = [];
    this.locationSearched = false;
    this.locationResultNameFilter = '';
    this.locationResultCityStateFilter = '';
  }
}
